import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import AuthLayout from "@/components/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const t = useT();
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [invite, setInvite] = useState(sp.get("invite") || "");
  const isBusiness = sp.get("audience") === "business";
  const [showInvite, setShowInvite] = useState(!!sp.get("invite") || isBusiness);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register({
        email: email.trim() || null,
        name: name.trim(),
        password,
        invite_token: invite.trim() || null,
      });
      toast.success(t("auth.inviteRedeemed"));
      nav("/app", { replace: true });
    } catch (e) {
      setErr(errMsg(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      eyebrow={isBusiness ? t("b2b.auth.businessEyebrow") : t("b2b.auth.personalEyebrow")}
      title={t("auth.createAccount")}
      subtitle={t("auth.createSub")}
      footer={
        <>
          {t("auth.haveAccount")}{" "}
          <Link to="/login" data-testid="register-goto-login" className="text-sage-600 font-semibold hover:underline">
            {t("auth.signIn")}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="reg-name">{t("common.name")}</Label>
          <Input id="reg-name" data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
            className="mt-1.5 h-11 rounded-xl bg-white" placeholder="Ada Lovelace" />
        </div>
        <div>
          <Label htmlFor="reg-email">{t("common.email")}</Label>
          <Input id="reg-email" data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 h-11 rounded-xl bg-white" placeholder="you@example.com" />
        </div>
        <div>
          <Label htmlFor="reg-password">{t("common.password")}</Label>
          <Input id="reg-password" data-testid="register-password" type="password" required minLength={10} value={password}
            onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" placeholder="••••••••••" />
          <p className="text-xs text-ink-500 mt-1">{t("auth.passwordHint")}</p>
        </div>
        {/* Invitation code is OPTIONAL: consumers register freely; B2B members paste their org invite. */}
        {showInvite ? (
          <div>
            <Label htmlFor="reg-invite">{t("auth.inviteToken")}</Label>
            <Input id="reg-invite" data-testid="register-invite" value={invite} onChange={(e) => setInvite(e.target.value)}
              className="mt-1.5 h-11 rounded-xl bg-white font-mono" placeholder="INVITE-XXXX" />
            <p className="text-xs text-ink-500 mt-1">{t("b2b.auth.inviteOptional")}</p>
          </div>
        ) : (
          <div className="text-xs text-ink-500 flex items-center justify-between gap-3">
            <span>{t("b2b.auth.noInviteNeeded")}</span>
            <button type="button" data-testid="register-toggle-invite" onClick={() => setShowInvite(true)} className="text-sage-600 font-semibold hover:underline whitespace-nowrap">
              {t("b2b.auth.haveInviteQ")}
            </button>
          </div>
        )}

        {err && (
          <div data-testid="register-error" className="rounded-xl bg-rust-500/10 border border-rust-500/20 text-rust-600 text-sm px-3 py-2">
            {err}
          </div>
        )}

        <Button type="submit" data-testid="register-submit" disabled={busy}
          className="w-full h-11 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium">
          {busy ? t("common.loading") : t("auth.signUp")}
        </Button>

      </form>
    </AuthLayout>
  );
}
